import React, { useEffect, useState } from 'react';
import { customersService, Customer } from '../services/customersService';
import { useTranslation } from '../hooks/useTranslation';

interface AddressFieldProps {
  customerId?: string | number;
}

const AddressField: React.FC<AddressFieldProps> = ({ customerId }) => {
  const { translate } = useTranslation();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!customerId) {
      setCustomer(null);
      return;
    }

    const fetchCustomer = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const customerData = await customersService.getCustomer(
          String(customerId)
        );
        setCustomer(customerData);
      } catch (err) {
        console.error('Error fetching customer address:', err);
        setError(err instanceof Error ? err.message : 'Failed to load address');
        setCustomer(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomer();
  }, [customerId]);

  if (!customerId) {
    return null;
  }

  if (isLoading) {
    return <span className="text-muted-foreground text-sm">Loading...</span>;
  }

  if (error || !customer) {
    return (
      <span className="text-muted-foreground text-sm italic">
        {translate('pos.orders.modern.addressNotAvailable')}
      </span>
    );
  }

  // Build address string from customer data
  const addressParts: string[] = [];
  if (customer.address) addressParts.push(customer.address);
  if (customer.city) addressParts.push(customer.city);
  if (customer.stateAbbr) addressParts.push(customer.stateAbbr);
  if (customer.zipcode) addressParts.push(customer.zipcode);

  const fullAddress = addressParts.join(', ');

  if (!fullAddress) {
    return (
      <span className="text-muted-foreground text-sm italic">
        {translate('pos.orders.modern.noAddressOnFile')}
      </span>
    );
  }

  return (
    <span className="text-sm">
      <span data-sensitive-field="address">{fullAddress}</span>
    </span>
  );
};

export default AddressField;
